import { useEffect, useMemo, useState } from 'react';
import {
  BadgeDollarSign,
  CircleCheck,
  CreditCard,
  FlaskConical,
  KeyRound,
  Pencil,
  Plus,
  Power,
  Radio,
  SmartphoneNfc,
  Trash2,
  Wrench,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  AppChannel,
  Currency,
  PaymentMethod,
  PaymentPlan,
  PaymentProviderId,
} from '../types';
import {
  ALL_PAYMENT_OPTIONS,
  PAYMENT_METHOD_META,
} from '../config/defaults';
import { paymentOptionKey } from '../types';
import { usePaymentStore } from '../state/usePaymentStore';
import { usePaymentService } from '../hooks/usePaymentService';
import { paymentApi, adminPaymentsApi, type FlexPayConfigResponse } from '../services/paymentApi';
import { formatPrice } from '../components/PlanCard';

/** Libellé lisible d'un canal, pour l'écran admin. */
const CHANNEL_LABEL: Record<AppChannel, string> = {
  [AppChannel.WEB]: 'Web / Stores (monde + RDC)',
  [AppChannel.MAXIT]: 'Super App Maxit (Orange)',
  [AppChannel.MPESA_SUPER_APP]: 'Super App M-Pesa',
};

/** Nom d'affichage d'un fournisseur (pour distinguer les options homonymes). */
const PROVIDER_LABEL: Record<PaymentProviderId, string> = {
  [PaymentProviderId.FLEXPAY]: 'FlexPay',
  [PaymentProviderId.MPESA]: 'M-Pesa',
  [PaymentProviderId.ORANGE]: 'Orange Money',
  [PaymentProviderId.MOCK]: 'Mock',
};

/**
 * Persiste la config et/ou les plans COURANTS (post-mutation du store) côté
 * serveur : c'est ce qui rend le réglage admin global à tous les visiteurs.
 * En cas d'échec, la modification reste locale et l'admin est prévenu.
 */
async function persistToServer(what: { config?: boolean; plans?: boolean }) {
  const state = usePaymentStore.getState();
  try {
    await paymentApi.saveSettings({
      ...(what.config ? { config: state.config } : {}),
      ...(what.plans ? { plans: state.plans } : {}),
    });
  } catch {
    toast.error(
      'Sauvegarde serveur impossible — le réglage n’est appliqué que sur ce navigateur.',
    );
  }
}

/**
 * ─── Administration des paiements ────────────────────────────────────────────
 * - Interrupteur général (payments ON/OFF) — OFF = plateforme 100 % libre
 * - Activation par provider + provider par défaut + provider actuellement résolu
 * - Mode Mock (tests) et mode Maintenance
 * - Éditeur des plans (prix, devise, features)
 * - Statistiques des paiements (depuis l'historique local en attendant l'API)
 *
 * CONTRAT BACKEND : brancher ces réglages sur GET/PATCH /admin/payments/* ;
 * le store et cette page ne changent pas, seules les actions d'hydratation.
 */

function Toggle({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      disabled={disabled}
      className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 disabled:opacity-40 ${
        checked ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600'
      }`}
    >
      <span
        className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${
          checked ? 'left-[22px]' : 'left-0.5'
        }`}
      />
    </button>
  );
}

function SettingRow({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-3.5 border-b border-gray-100 dark:border-gray-700 last:border-0">
      <div>
        <p className="text-sm font-medium text-gray-900 dark:text-white">{title}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">{description}</p>
      </div>
      {children}
    </div>
  );
}

const EMPTY_PLAN: PaymentPlan = {
  id: '',
  name: '',
  description: '',
  price: 5,
  currency: 'USD',
  periodDays: 30,
  entitlements: { dailyPracticeLimit: null, dailySimulationLimit: null },
  features: [],
  isActive: true,
};

/** Résumé compact des limites d'un plan pour la liste admin. */
function entitlementsSummary(plan: PaymentPlan): string {
  const fmt = (v: number | null) => (v === null ? 'illimité' : `${v}/jour`);
  return `Pratique : ${fmt(plan.entitlements.dailyPracticeLimit)} · Simulations : ${fmt(plan.entitlements.dailySimulationLimit)}`;
}

/**
 * Champ de limite quotidienne : nombre entier positif, vide = illimité.
 * C'est CE réglage (pas le texte des avantages) que le QuotaGate applique.
 */
function LimitField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number | null;
  onChange: (v: number | null) => void;
}) {
  return (
    <label className="flex-1">
      <span className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">
        {label}
      </span>
      <input
        type="number"
        min={1}
        step={1}
        value={value ?? ''}
        placeholder="Illimité"
        onChange={(e) => {
          const n = parseInt(e.target.value, 10);
          onChange(Number.isFinite(n) && n > 0 ? n : null);
        }}
        className="w-full px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white placeholder-gray-400"
      />
    </label>
  );
}

/** Pastille d'état d'un moyen de paiement (configuré → disponible, ou coupé). */
function StatusBadge({ label, active }: { label: string; active: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ${
        active
          ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
          : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
      }`}
    >
      {active ? <CircleCheck className="w-3 h-3" /> : <X className="w-3 h-3" />}
      {label} · {active ? 'disponible' : 'désactivé'}
    </span>
  );
}

/**
 * Credentials FlexPay éditables depuis le dashboard (stockés en base, priment
 * sur les variables d'env du serveur). Le token n'est jamais renvoyé en clair :
 * on affiche un aperçu et on ne l'écrase que si l'admin en saisit un nouveau.
 */
function FlexPayCredentials() {
  const [cfg, setCfg] = useState<FlexPayConfigResponse | null>(null);
  const [form, setForm] = useState({
    apiUrl: '',
    merchant: '',
    callbackUrlBase: '',
    cardUrl: '',
    token: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminPaymentsApi
      .getFlexPayConfig()
      .then(({ data }) => {
        setCfg(data);
        setForm({
          apiUrl: data.apiUrl,
          merchant: data.merchant,
          callbackUrlBase: data.callbackUrlBase,
          cardUrl: data.cardUrl,
          token: '',
        });
      })
      .catch(() => toast.error('Impossible de charger la configuration FlexPay.'))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await adminPaymentsApi.saveFlexPayConfig({
        apiUrl: form.apiUrl.trim(),
        merchant: form.merchant.trim(),
        callbackUrlBase: form.callbackUrlBase.trim(),
        cardUrl: form.cardUrl.trim(),
        // Champ vide = on conserve le token existant côté serveur.
        token: form.token.trim() || undefined,
      });
      setCfg(data);
      setForm((f) => ({ ...f, token: '' }));
      toast.success('Credentials FlexPay enregistrés.');
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? "Échec de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    'w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white';

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center">
          <KeyRound className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Accès FlexPay
          </h2>
          <p className="text-xs text-gray-500">
            Mobile money RDC. Ces réglages priment sur les variables
            d'environnement du serveur.
          </p>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Chargement…</p>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
              Code marchand
            </label>
            <input
              className={inputCls}
              value={form.merchant}
              onChange={(e) => setForm((f) => ({ ...f, merchant: e.target.value }))}
              placeholder="ex. faithful2022"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
              Token FlexPay{' '}
              {cfg?.tokenSet && (
                <span className="ml-1 inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">
                  <CircleCheck className="w-3 h-3" />
                  défini ({cfg.tokenPreview})
                </span>
              )}
            </label>
            <input
              type="password"
              autoComplete="off"
              className={inputCls}
              value={form.token}
              onChange={(e) => setForm((f) => ({ ...f, token: e.target.value }))}
              placeholder={
                cfg?.tokenSet
                  ? 'Laisser vide pour conserver le token actuel'
                  : 'Coller le token FlexPay (nu, sans « Bearer »)'
              }
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
              URL de l'API{' '}
              <span className="text-gray-400 font-normal">
                — mobile money + vérification carte
              </span>
            </label>
            <input
              className={inputCls}
              value={form.apiUrl}
              onChange={(e) => setForm((f) => ({ ...f, apiUrl: e.target.value }))}
              placeholder="http://41.243.7.46:3006/flexpay/api/rest/v1"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
              URL de paiement carte bancaire{' '}
              <span className="text-gray-400 font-normal">— optionnel</span>
            </label>
            <input
              className={inputCls}
              value={form.cardUrl}
              onChange={(e) => setForm((f) => ({ ...f, cardUrl: e.target.value }))}
              placeholder="https://cardpayment.flexpay.cd/v1.1/pay"
            />
            <p className="mt-1 text-[11px] text-gray-400">
              Vide = paiement par carte bancaire désactivé (le moyen disparaît de
              l'UI). Renseignée avec le token, le code marchand et l'URL de l'API,
              la carte devient disponible.
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
              URL publique du backend (callback){' '}
              <span className="text-gray-400 font-normal">— optionnel</span>
            </label>
            <input
              className={inputCls}
              value={form.callbackUrlBase}
              onChange={(e) =>
                setForm((f) => ({ ...f, callbackUrlBase: e.target.value }))
              }
              placeholder="https://api.exam-copilot.com"
            />
            <p className="mt-1 text-[11px] text-gray-400">
              Vide = pas de callback (le statut est récupéré par polling).
            </p>
          </div>

          {cfg && (
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <StatusBadge
                label="Mobile Money"
                active={cfg.mobileMoneyConfigured}
              />
              <StatusBadge label="Carte bancaire" active={cfg.cardConfigured} />
            </div>
          )}

          <div className="flex justify-end">
            <button
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white rounded-lg text-sm font-semibold"
            >
              {saving ? 'Enregistrement…' : 'Enregistrer les accès'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function PaymentAdminPage() {
  const service = usePaymentService();
  const config = usePaymentStore((s) => s.config);
  const plans = usePaymentStore((s) => s.plans);
  const updateConfig = usePaymentStore((s) => s.updateConfig);
  const setProviderEnabled = usePaymentStore((s) => s.setProviderEnabled);
  const setOptionEnabled = usePaymentStore((s) => s.setOptionEnabled);
  const upsertPlan = usePaymentStore((s) => s.upsertPlan);
  const removePlan = usePaymentStore((s) => s.removePlan);

  const [editing, setEditing] = useState<PaymentPlan | null>(null);
  const [featuresText, setFeaturesText] = useState('');

  // Toute mutation = application locale immédiate + persistance serveur.
  const patchConfig = (patch: Parameters<typeof updateConfig>[0]) => {
    updateConfig(patch);
    void persistToServer({ config: true });
  };
  const toggleProvider = (id: PaymentProviderId, enabled: boolean) => {
    setProviderEnabled(id, enabled);
    void persistToServer({ config: true });
  };
  const toggleOption = (
    method: PaymentMethod,
    provider: PaymentProviderId,
    enabled: boolean,
  ) => {
    setOptionEnabled(method, provider, enabled);
    void persistToServer({ config: true });
  };

  // Options réellement proposées sur le canal où l'admin est actuellement.
  const availableMethods = useMemo(() => {
    try {
      return service.getAvailableOptions().map((o) => o.method);
    } catch {
      return [];
    }
  }, [service, config]);

  const openEditor = (plan: PaymentPlan | null) => {
    const p = plan ?? { ...EMPTY_PLAN, id: `plan-${Date.now().toString(36)}` };
    setEditing(p);
    setFeaturesText(p.features.join('\n'));
  };

  const savePlan = () => {
    if (!editing) return;
    if (!editing.name.trim() || editing.price <= 0) {
      toast.error('Nom et prix (positif) sont requis.');
      return;
    }
    upsertPlan({
      ...editing,
      features: featuresText.split('\n').map((f) => f.trim()).filter(Boolean),
    });
    void persistToServer({ plans: true });
    setEditing(null);
    toast.success('Plan enregistré');
  };

  const providerRows: { id: PaymentProviderId; name: string; note: string }[] = [
    { id: PaymentProviderId.FLEXPAY, name: 'FlexPay', note: 'Carte bancaire + mobile money (web, RDC)' },
    { id: PaymentProviderId.MPESA, name: 'M-Pesa', note: 'Carte Visa M-Pesa + M-Pesa (Super App)' },
    { id: PaymentProviderId.ORANGE, name: 'Orange Money', note: 'Super App Maxit (Orange)' },
    { id: PaymentProviderId.MOCK, name: 'Mock', note: 'Simulateur de test — aucun paiement réel' },
  ];

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Paiements</h1>
        <p className="text-gray-500 dark:text-gray-400">
          Module de paiement, fournisseurs, plans et statistiques
        </p>
      </div>

      {/* ── Interrupteur général ── */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center">
            <Power className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Module de paiement</h2>
            <p className="text-xs text-gray-500">
              Désactivé : la plateforme entière fonctionne librement, aucun écran de paiement n'existe.
            </p>
          </div>
        </div>
        <SettingRow
          title="Activer les paiements"
          description={config.enabled ? 'Abonnement requis pour les fonctionnalités premium' : 'Accès libre à toutes les fonctionnalités'}
        >
          <Toggle checked={config.enabled} onChange={(v) => patchConfig({ enabled: v })} />
        </SettingRow>
        <SettingRow
          title="Mode maintenance paiement"
          description="Écrans visibles mais transactions suspendues"
        >
          <div className="flex items-center gap-2">
            {config.maintenanceMode && <Wrench className="w-4 h-4 text-amber-500" />}
            <Toggle
              checked={config.maintenanceMode}
              onChange={(v) => patchConfig({ maintenanceMode: v })}
              disabled={!config.enabled}
            />
          </div>
        </SettingRow>
        <SettingRow
          title="Mode Mock (tests)"
          description="Force le fournisseur simulé : tout le flow sans paiement réel"
        >
          <div className="flex items-center gap-2">
            {config.mockMode && <FlaskConical className="w-4 h-4 text-indigo-500" />}
            <Toggle
              checked={config.mockMode}
              onChange={(v) => patchConfig({ mockMode: v })}
              disabled={!config.enabled}
            />
          </div>
        </SettingRow>
      </div>

      {/* ── Fournisseurs ── */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center">
            <CreditCard className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Fournisseurs</h2>
            <p className="text-xs text-gray-500">
              Canal courant :{' '}
              <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                {CHANNEL_LABEL[service.channel]}
              </span>{' '}
              — options actives :{' '}
              {availableMethods.length
                ? availableMethods.map((m) => PAYMENT_METHOD_META[m].label).join(', ')
                : 'aucune'}
            </p>
          </div>
        </div>

        {providerRows.map((p) => (
          <SettingRow key={p.id} title={p.name} description={p.note}>
            <div className="flex items-center gap-3">
              {config.defaultProviderId === p.id && (
                <span className="text-[10px] font-bold uppercase tracking-wide text-emerald-600 dark:text-emerald-400">
                  défaut
                </span>
              )}
              <Toggle
                checked={config.providers[p.id]}
                onChange={(v) => toggleProvider(p.id, v)}
              />
            </div>
          </SettingRow>
        ))}

        <div className="mt-4 flex items-center gap-3">
          <Radio className="w-4 h-4 text-gray-400" />
          <label className="text-sm text-gray-700 dark:text-gray-300">Fournisseur par défaut :</label>
          <select
            value={config.defaultProviderId}
            onChange={(e) =>
              patchConfig({ defaultProviderId: e.target.value as PaymentProviderId })
            }
            className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
          >
            {providerRows
              .filter((p) => p.id !== PaymentProviderId.MOCK)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
          </select>
        </div>
      </div>

      {/* ── Accès FlexPay (credentials éditables) ── */}
      <FlexPayCredentials />

      {/* ── Moyens de paiement (kill-switch par méthode) ── */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center">
            <SmartphoneNfc className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Moyens de paiement</h2>
            <p className="text-xs text-gray-500">
              Coupez un moyen le temps d'une maintenance — les autres restent
              disponibles. Un canal sans aucun moyen actif affiche « paiement
              indisponible ».
            </p>
          </div>
        </div>

        {ALL_PAYMENT_OPTIONS.map(({ method, provider, channels }) => {
          const key = paymentOptionKey(method, provider);
          const enabled = config.optionEnabled?.[key] !== false;
          return (
            <SettingRow
              key={key}
              title={`${PAYMENT_METHOD_META[method].label} · ${PROVIDER_LABEL[provider]}`}
              description={`${PAYMENT_METHOD_META[method].hint} · ${channels
                .map((c) => CHANNEL_LABEL[c])
                .join(' + ')}`}
            >
              <div className="flex items-center gap-2">
                {!enabled && <Wrench className="w-4 h-4 text-amber-500" />}
                <Toggle
                  checked={enabled}
                  onChange={(v) => toggleOption(method, provider, v)}
                  disabled={!config.enabled}
                />
              </div>
            </SettingRow>
          );
        })}
      </div>

      {/* ── Plans ── */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center">
              <BadgeDollarSign className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Abonnements mensuels</h2>
          </div>
          <button
            onClick={() => openEditor(null)}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold"
          >
            <Plus className="w-4 h-4" /> Nouveau plan
          </button>
        </div>

        <div className="space-y-2">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className="flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-100 dark:border-gray-700"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  {plan.name}{' '}
                  {!plan.isActive && (
                    <span className="text-[10px] text-gray-400 uppercase">(inactif)</span>
                  )}
                </p>
                <p className="text-xs text-gray-500 truncate">{plan.description}</p>
                <p className="text-[10px] text-emerald-600 dark:text-emerald-400 truncate mt-0.5">
                  {entitlementsSummary(plan)}
                </p>
              </div>
              <p className="text-sm font-bold text-gray-900 dark:text-white">
                {formatPrice(plan.price, plan.currency)}/mois
              </p>
              <button
                onClick={() => openEditor(plan)}
                className="p-2 text-gray-400 hover:text-emerald-600 transition-colors"
              >
                <Pencil className="w-4 h-4" />
              </button>
              <button
                onClick={() => {
                  removePlan(plan.id);
                  void persistToServer({ plans: true });
                  toast.success('Plan supprimé');
                }}
                className="p-2 text-gray-400 hover:text-red-500 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Les statistiques réelles (revenus, taux de succès) vivent sur le
          Dashboard admin et la page Transactions — calculées depuis le registre
          serveur, pas l'historique local. */}

      {/* ── Éditeur de plan (modal) ── */}
      {editing && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setEditing(null)} />
          <div className="relative w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 max-h-[85vh] overflow-y-auto">
            <button
              onClick={() => setEditing(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {plans.some((p) => p.id === editing.id) ? 'Modifier le plan' : 'Nouveau plan'}
            </h3>
            <div className="space-y-3">
              <input
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                placeholder="Nom du plan"
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
              />
              <input
                value={editing.description}
                onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                placeholder="Description courte"
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
              />
              <div className="flex gap-3">
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={editing.price}
                  onChange={(e) => setEditing({ ...editing, price: Number(e.target.value) })}
                  placeholder="Prix"
                  className="flex-1 px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
                />
                <select
                  value={editing.currency}
                  onChange={(e) => setEditing({ ...editing, currency: e.target.value as Currency })}
                  className="px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
                >
                  <option value="USD">USD ($)</option>
                  <option value="CDF">CDF (FC)</option>
                </select>
              </div>

              {/* ── Limites RÉELLEMENT appliquées (entitlements) ── */}
              <div className="rounded-lg border border-emerald-200 dark:border-emerald-800/50 bg-emerald-50/50 dark:bg-emerald-900/10 p-3">
                <p className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-400 uppercase tracking-wide mb-2">
                  Limites du plan (appliquées aux abonnés)
                </p>
                <div className="flex gap-3">
                  <LimitField
                    label="Pratiques / jour"
                    value={editing.entitlements.dailyPracticeLimit}
                    onChange={(v) =>
                      setEditing({
                        ...editing,
                        entitlements: { ...editing.entitlements, dailyPracticeLimit: v },
                      })
                    }
                  />
                  <LimitField
                    label="Simulations / jour"
                    value={editing.entitlements.dailySimulationLimit}
                    onChange={(v) =>
                      setEditing({
                        ...editing,
                        entitlements: { ...editing.entitlements, dailySimulationLimit: v },
                      })
                    }
                  />
                </div>
                <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-2">
                  Champ vide = illimité. Ces limites sont affichées automatiquement sur
                  la carte du plan et appliquées par l'application — le texte ci-dessous
                  ne sert qu'aux avantages additionnels.
                </p>
              </div>

              <textarea
                value={featuresText}
                onChange={(e) => setFeaturesText(e.target.value)}
                placeholder={'Avantages additionnels (un par ligne) — les limites ci-dessus s\'affichent automatiquement'}
                rows={3}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
              />
              <div className="flex items-center justify-between">
                <label className="text-sm text-gray-700 dark:text-gray-300">Plan actif</label>
                <Toggle
                  checked={editing.isActive}
                  onChange={(v) => setEditing({ ...editing, isActive: v })}
                />
              </div>
              <div className="flex items-center justify-between">
                <label className="text-sm text-gray-700 dark:text-gray-300">Mis en avant</label>
                <Toggle
                  checked={Boolean(editing.highlighted)}
                  onChange={(v) => setEditing({ ...editing, highlighted: v })}
                />
              </div>
              <button
                onClick={savePlan}
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold"
              >
                <CircleCheck className="w-4 h-4" /> Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
